import { createPagesBrowserClient } from "@supabase/auth-helpers-nextjs";

const missingConfigError = {
  message: "Supabase is not configured for this environment.",
};

const createNoopQueryBuilder = () => {
  const listResult = { data: [], error: null };
  const singleResult = { data: null, error: null };

  const builder: {
    delete: () => typeof builder;
    eq: () => typeof builder;
    insert: () => Promise<{ data: null; error: typeof missingConfigError }>;
    limit: () => typeof builder;
    order: () => typeof builder;
    select: () => typeof builder;
    single: () => Promise<typeof singleResult>;
    then: PromiseLike<typeof listResult>["then"];
  } = {
    select: () => builder,
    eq: () => builder,
    order: () => builder,
    limit: () => builder,
    delete: () => builder,
    insert: async () => ({ data: null, error: missingConfigError }),
    single: async () => singleResult,
    then: (onfulfilled, onrejected) =>
      Promise.resolve(listResult).then(onfulfilled, onrejected),
  };

  return builder;
};

const createNoopSupabaseClient = () =>
  ({
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({
        data: {
          subscription: {
            unsubscribe: () => undefined,
          },
        },
      }),
      resetPasswordForEmail: async () => ({
        data: null,
        error: missingConfigError,
      }),
      signInWithOtp: async () => ({
        data: { session: null, user: null },
        error: missingConfigError,
      }),
      signInWithPassword: async () => ({
        data: { session: null, user: null },
        error: missingConfigError,
      }),
      updateUser: async () => ({
        data: { user: null },
        error: missingConfigError,
      }),
    },
    from: () => createNoopQueryBuilder(),
  }) as unknown as ReturnType<typeof createPagesBrowserClient>;

export const hasSupabaseBrowserEnv = () =>
  !!process.env.NEXT_PUBLIC_SUPABASE_URL &&
  !!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

export const createBrowserSupabaseClient = () => {
  if (hasSupabaseBrowserEnv()) {
    return createPagesBrowserClient();
  }

  return createNoopSupabaseClient();
};
